import { z } from 'zod'

export const loginRequestSchema = z.object({
  email: z.email(),
  password: z.string().min(1),
})
export type LoginRequest = z.infer<typeof loginRequestSchema>

/**
 * WHO IS SIGNED IN — and, separately, WHICH TENANT THEY ARE WORKING IN.
 *
 * The UI spec said "tenant name and user, right-aligned, permanent — never a
 * dropdown in v1". That is now wrong on both counts, by the founder's ruling
 * of 2026-08-22: the console supervises many tenants, so the tenant *is* a
 * dropdown, and it is a selection rather than a property of the account.
 *
 * There is no `tenantName` here on purpose. The console holds no tenants
 * table, so it has no name to serve — the display name comes from Cadence's
 * registry through the switcher. Serving a name from here would be the
 * console asserting a fact about a tenant it is not the registry for, which is
 * precisely how it came to render one organisation's name over another's runs.
 */
export const meResponseSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  /** One value in v1, `admin`, and it carries scope over every tenant. */
  role: z.string(),
  /** A Cadence UUID — the tenant this session is currently working in. */
  currentTenantId: z.string(),
})
export type MeResponse = z.infer<typeof meResponseSchema>

export const passwordResetRequestSchema = z.object({
  email: z.email(),
})
export type PasswordResetRequest = z.infer<typeof passwordResetRequestSchema>

export const passwordResetConfirmSchema = z.object({
  token: z.string().min(1),
  // Not specified in the tech stack spec; 8 characters is a floor, not a
  // policy — length-and-uniqueness guidance (NIST 800-63B) over composition
  // rules, but that whole conversation is a v2+ concern, not a v1 blocker.
  newPassword: z.string().min(8),
})
export type PasswordResetConfirm = z.infer<typeof passwordResetConfirmSchema>

export const messageResponseSchema = z.object({
  message: z.string(),
})

export const csrfTokenResponseSchema = z.object({
  csrfToken: z.string(),
})

export const errorResponseSchema = z.object({
  error: z.string(),
  statusCode: z.number(),
})
